import type { ReactElement } from 'react';
import { Box, Group, Stack, Text } from '@mantine/core';
import { Minus, TrendingDown, TrendingUp, type LucideIcon } from 'lucide-react';
import { SurfaceCard } from '@/design/SurfaceCard';
import Sparkline from '@/design/Sparkline/Sparkline';
import AdoptionScoreBar from './AdoptionScoreBar';
import { adoptionBand } from './AdoptionBands';
import type { AdoptionDriver } from './AdoptionTypes';

interface AdoptionMetricCardProps {
    driver: AdoptionDriver;
    icon: LucideIcon;
}

function DeltaBadge({ pts }: { pts: number }): ReactElement {
    const dir = pts > 0 ? 'up' : pts < 0 ? 'down' : 'flat';
    const color = dir === 'up' ? 'var(--bm-color-success)' : dir === 'down' ? 'var(--bm-color-danger)' : 'var(--bm-color-text-dimmed)';
    const Icon = dir === 'up' ? TrendingUp : dir === 'down' ? TrendingDown : Minus;
    return (
        <Group gap={3} wrap="nowrap" align="center" style={{ color }}>
            <Icon size={13} />
            <Text fw={700} style={{ fontSize: 12 }}>
                {pts > 0 ? '+' : ''}
                {pts} pts
            </Text>
        </Group>
    );
}

/**
 * One adoption driver as a KPI card: the score against its maturity band (via
 * {@link AdoptionScoreBar}), the 4-week delta, and the weekly trend sparkline.
 * Replaces the generic SignalTile so the score reads with a "what's good" scale.
 */
export default function AdoptionMetricCard({ driver, icon: Icon }: AdoptionMetricCardProps): ReactElement {
    // The card accent tracks the score's maturity band, matching its score bar (and the drivers drill-in).
    const band = adoptionBand(driver.score);
    const color = band.color;
    return (
        <SurfaceCard $padding="md" style={{ height: '100%', borderTop: `2px solid ${color}`, display: 'flex', flexDirection: 'column' }}>
            <Group gap="sm" wrap="nowrap" align="flex-start" mb={6}>
                <Box
                    style={{
                        width: 30,
                        height: 30,
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: band.soft,
                        border: `1px solid color-mix(in srgb, ${color} 28%, transparent)`,
                        borderRadius: 'var(--bm-radius)',
                    }}
                >
                    <Icon size={15} color={color} />
                </Box>
                <Box style={{ flex: 1, minWidth: 0 }}>
                    <Text fw={700} truncate style={{ fontSize: 14, letterSpacing: '-0.02em', lineHeight: 1.2 }}>
                        {driver.label}
                    </Text>
                    <Text size="xs" c="dimmed" style={{ fontSize: 11 }}>
                        weight {driver.weight}%
                    </Text>
                </Box>
            </Group>

            <Text size="xs" c="dimmed" lh={1.45} mb="sm" lineClamp={2} style={{ fontSize: 11 }}>
                {driver.description}
            </Text>

            <Stack gap={8} mt="auto">
                <Group justify="space-between" align="flex-end" wrap="nowrap">
                    <Group gap={8} align="baseline" wrap="nowrap">
                        <Text fw={700} ff="monospace" style={{ fontSize: 30, lineHeight: 1, letterSpacing: '-0.03em', color }}>
                            {driver.score}
                        </Text>
                        <Text fw={600} style={{ fontSize: 11, color: band.color }}>
                            {band.name}
                        </Text>
                    </Group>
                    <DeltaBadge pts={driver.deltaPts} />
                </Group>

                <AdoptionScoreBar value={driver.score} />

                <Sparkline values={driver.weekly} color={color} height={34} />
            </Stack>
        </SurfaceCard>
    );
}
